import { createRef } from "react";

import type { ILocation } from "@/src/contexts/LocationContext";
import type { IComponent } from "@Components/Component";
import { Component } from "@Components/Component";
import { type IMap, Map, MapMarker } from "@Components/Map";
import { scrollToY } from "@Components/Modules/Helpers";
import type { IPanel } from "@Components/Panel";
import { Panel } from "@Components/Panel";

import type { IShoppingMallMapMarker } from "../map/ShoppingMallMapMarker";
import { ShoppingMallMapMarkerInfo } from "../map/ShoppingMallMapMarker";
import type { IKeyValueOption, IShoppingMallsFiltersMap } from "./ShoppingMallsFiltersMap";
import { ShoppingMallsFiltersMap } from "./ShoppingMallsFiltersMap";

import * as css from "./ShoppingMallsFilters.module.css";

export interface IShoppingMallsFiltersListsMap extends IComponent {
    filteredShoppingMalls: IShoppingMallsFiltersMap;
    filteredShoppingMallsMapPanel: IPanel;
    mapComponent: IMap;
    additionalServiceList: IKeyValueOption[];
    brandsList: IKeyValueOption[];
    selectedServices: IKeyValueOption[];
    selectedBrands: IKeyValueOption[];
    location: ILocation;
}

interface IShoppingMallsFiltersListsMapState {
    selectedMarker: IShoppingMallMapMarker | null;
    filteredPanel: IShoppingMallsFiltersMap | null;
    resultsKey: string;
}

export class ShoppingMallsFiltersListsMap extends Component<
    IShoppingMallsFiltersListsMap,
    IShoppingMallsFiltersListsMapState
> {
    private mapPanelRef = createRef<HTMLDivElement>();

    constructor(props: IShoppingMallsFiltersListsMap) {
        super(props);

        this.state = {
            selectedMarker: null,
            filteredPanel: this.props.filteredShoppingMalls ? { ...this.props.filteredShoppingMalls } : null,
            resultsKey: ShoppingMallsFiltersListsMap.getUniqueKey(this.props),
        };

        this.renderMarker = this.renderMarker.bind(this);
        this.showLocationClick = this.showLocationClick.bind(this);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IShoppingMallsFiltersListsMap): void {
        const reload = this.shouldReload(nextProps);

        if (reload) {
            this.setState({
                resultsKey: ShoppingMallsFiltersListsMap.getUniqueKey(nextProps),
                selectedMarker: null,
            });
        }
    }

    private static getTexts(arr: IKeyValueOption[]): string {
        return arr.reduce((accumulator, currentValue) => accumulator + currentValue.text, "");
    }

    private static getUniqueKey(props: IShoppingMallsFiltersListsMap): string {
        return (
            ShoppingMallsFiltersListsMap.getTexts(props.selectedServices) +
            ShoppingMallsFiltersListsMap.getTexts(props.selectedBrands)
        );
    }

    private mapDefaultPosition = () =>
        this.props.location
            ? { lat: this.props.location.latitude, lng: this.props.location.longitude }
            : this.props.mapComponent.defaultPosition;

    private mapPanel = (): JSX.Element | null => {
        if (!this.props.filteredShoppingMallsMapPanel) {
            return null;
        }

        if (!this.props.mapComponent) {
            return null;
        }

        const mapOptions = this.state.selectedMarker
            ? { ...Map.DefaultMapOptions, center: this.state.selectedMarker.position, zoom: 15 }
            : Map.DefaultMapOptions;

        return (
            <Panel
                {...this.props.filteredShoppingMallsMapPanel}
                ref={this.mapPanelRef}
                className={css.shoppingMallsMapPanel}
            >
                <Map
                    key={this.state.resultsKey}
                    mapOptions={mapOptions}
                    {...this.props.mapComponent}
                    defaultPosition={this.mapDefaultPosition()}
                    renderMarker={this.renderMarker}
                />
            </Panel>
        );
    };

    private shouldReload(nextProps: IShoppingMallsFiltersListsMap) {
        if (
            ShoppingMallsFiltersListsMap.getTexts(this.props.selectedServices) !==
            ShoppingMallsFiltersListsMap.getTexts(nextProps.selectedServices)
        ) {
            return true;
        }

        if (
            ShoppingMallsFiltersListsMap.getTexts(this.props.selectedBrands) !==
            ShoppingMallsFiltersListsMap.getTexts(nextProps.selectedBrands)
        ) {
            return true;
        }

        return false;
    }

    private showLocationClick(marker: IShoppingMallMapMarker): void {
        this.setState({ selectedMarker: marker });

        if (!this.mapPanelRef.current) {
            return;
        }

        scrollToY(this.mapPanelRef.current.offsetTop);
    }

    private renderMarker(props: IShoppingMallMapMarker): JSX.Element {
        const markerInfo = (markerProps: IShoppingMallMapMarker) => (
            <ShoppingMallMapMarkerInfo {...markerProps.info} />
        );

        const isOpen: { showInfo?: boolean } = {};
        if (this.state.selectedMarker) {
            isOpen.showInfo = props.id === this.state.selectedMarker.id;
        }

        const onOpen = () => {
            if (this.state.selectedMarker && this.state.selectedMarker.id !== props.id) {
                this.setState({ selectedMarker: null });
            }

            if (props.onOpenInfo) {
                props.onOpenInfo();
            }
        };

        return <MapMarker {...props} renderInfoContent={markerInfo} {...isOpen} onOpenInfo={onOpen} />;
    }

    public render(): JSX.Element {
        let filtered = null;
        if (this.state.filteredPanel) {
            filtered = {
                ...this.state.filteredPanel,
                additionalServiceList: this.props.additionalServiceList,
                brandsList: this.props.brandsList,
                selectedServices: this.props.selectedServices,
                selectedBrands: this.props.selectedBrands,
                onMapMarkerClick: this.showLocationClick,
            };
        }

        return (
            <div className={css.shoppingMallsFiltersListsMapComponent}>
                <ShoppingMallsFiltersMap {...filtered} />
                {this.mapPanel()}
            </div>
        );
    }
}
